import api from "./client";

// Whitelist kept in sync with `backend/app/schemas/leads.py::SortKey`.
// Any new value here MUST also exist in the backend Literal, otherwise
// the request will 422.
export type SortKey =
  | "created_at_desc"
  | "created_at_asc"
  | "amount_desc"
  | "amount_asc"
  | "modified_at_desc";

export interface LeadListItem {
  id: string;
  created_at: string;
  modified_at: string | null;
  client_email: string | null;
  client_phone: string | null;
  client_name_first: string | null;
  client_name_last: string | null;
  client_state: string | null;
  // Pydantic v2 serialises Decimal as a JSON string by default.
  client_amount: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  lead_type: string | null;
  model_type: string | null;
  number_verified: boolean | null;
  sugar_case_id: string | null;
  sugar_status: string | null;
}

export interface LeadsPage {
  items: LeadListItem[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export interface LeadDetail extends LeadListItem {
  raw: Record<string, unknown>;
  fetched_at: string;
  updated_at: string;
}

// Field names mirror the backend query params exactly
// (`backend/app/api/leads.py::leads_filter`). Don't rename them here
// without renaming them there too.
export interface LeadsFilter {
  q?: string;
  date_from?: string;
  date_to?: string;
  // IANA timezone (e.g. "Europe/Madrid"). Sent automatically by
  // `listLeads` / `exportLeadsCsv` so the backend can interpret the
  // calendar dates above in the user's local day, not in UTC.
  tz?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  client_state?: string;
  sugar_status?: string;
  model_type?: string;
  lead_type?: string;
  min_amount?: number;
  max_amount?: number;
  number_verified?: boolean;
  sort?: SortKey;
  page?: number;
  per_page?: number;
}

// Resolved once per session — `Intl.DateTimeFormat` is fairly cheap
// but there's no point recomputing on every request.
const BROWSER_TZ: string =
  Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

function toParams(
  f: LeadsFilter | undefined,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  if (!f) return out;
  for (const [k, v] of Object.entries(f)) {
    if (v === undefined || v === "" || v === null) continue;
    out[k] = v as string | number | boolean;
  }
  // Always attach the user's timezone when a date filter is in play
  // so the backend interprets the calendar dates as the user's local
  // day, not as UTC. Doesn't hurt to send it unconditionally either.
  if ((out.date_from || out.date_to) && !out.tz) {
    out.tz = BROWSER_TZ;
  }
  return out;
}

export async function listLeads(filter: LeadsFilter): Promise<LeadsPage> {
  const { data } = await api.get<LeadsPage>("/api/leads", {
    params: toParams(filter),
  });
  return data;
}

export async function getLead(id: string): Promise<LeadDetail> {
  const { data } = await api.get<LeadDetail>(
    `/api/leads/${encodeURIComponent(id)}`,
  );
  return data;
}

/**
 * Trigger a CSV export as a file download. Uses the same filter as
 * the list view, streamed server-side. We request a Blob and let the
 * browser save it via a synthetic <a download>.
 */
export async function exportLeadsCsv(filter: LeadsFilter): Promise<void> {
  const resp = await api.get("/api/leads/export", {
    params: toParams(filter),
    responseType: "blob",
  });
  const blob = resp.data as Blob;
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  const ts = new Date().toISOString().replace(/[:.]/g, "-");
  link.download = `leads-${ts}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
